import { FC, useMemo, useRef } from 'react';
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import { DailyNavReviewActionLog } from 'api';
import { DailyNavRecordWithDay, isFundManaged } from 'pages/Intelligence/DailyNav/daily-nav.api.types';
import { DAILY_NAV_REVIEW_FIELD_LABELS } from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { shortDate } from 'utilities';
import { ReviewPriceDialogContentProps } from './ReviewPriceDialog.props';
import { reviewPriceDialogColors as colors } from './ReviewPriceDialog.styles';

const fieldLabelSx = { color: colors.slate700, fontSize: 12, fontWeight: 600 };

// Shared by both toggle groups so the second one cannot drift from the first.
const toggleGroupSx = {
  '& .MuiToggleButton-root': {
    border: `1px solid ${colors.border}`,
    color: colors.slate700,
    fontSize: 11,
    lineHeight: 1.2,
    paddingX: 1.5,
    paddingY: 0.5,
    textTransform: 'none',
    '&.Mui-selected': {
      backgroundColor: colors.navy,
      color: '#fff',
      '&:hover': { backgroundColor: colors.navyHover },
    },
  },
};

const formatCurrency = (value: number | null | undefined, currencyCode = 'USD', fractionDigits = 0) =>
  isNil(value)
    ? '—'
    : Number(value).toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

const formatNoteTime = (value?: string) => {
  if (!value) {
    return '';
  }
  try {
    return format(parseISO(value), 'MMM d, yyyy, h:mm a');
  } catch {
    return value;
  }
};

const ReviewPriceDialogContent: FC<ReviewPriceDialogContentProps> = ({
  row,
  formValues,
  setFormValues,
  notes,
  noteDraft,
  setNoteDraft,
  noteFiles,
  setNoteFiles,
  onAddNote,
  onDeleteNote,
  onDownloadAttachment,
  reviewFieldsLocked = false,
  lockedReason,
  notesLocked = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const record = row?.record as DailyNavRecordWithDay | undefined;
  // The row's own anchor, not the firm's current setting.
  const fundManaged = isFundManaged(record);
  // Shown whether or not it has been worked, so the analyst sees why it is red.
  const isCarryingAnAdjustment = Boolean(record?.pending_price_adjustment);
  const previousValuation = row?.previousValuation;
  const currencyCode = previousValuation?.cap_table_currency || 'USD';
  let priceAdjustedValue: 'yes' | 'no' | null = null;
  if (formValues.price_adjusted === true) {
    priceAdjustedValue = 'yes';
  } else if (formValues.price_adjusted === false) {
    priceAdjustedValue = 'no';
  }

  // At most one audit entry per field (the latest), keyed for lookup beside
  // each control. Absent when the field has never been changed.
  const latestAuditByField = useMemo(() => {
    const byField: Partial<Record<DailyNavReviewActionLog.field, DailyNavReviewActionLog>> = {};
    (row?.record.review_action_logs ?? []).forEach(log => {
      if (log.field) {
        byField[log.field] = log;
      }
    });
    return byField;
  }, [row]);

  const renderAudit = (field: DailyNavReviewActionLog.field) => {
    const log = latestAuditByField[field];
    if (!log) {
      return null;
    }
    return (
      <Typography sx={{ color: colors.slate500, fontSize: 10 }} data-testid={`review-price-audit-${field}`}>
        {log.created_by_name || 'Unknown'} · {formatNoteTime(log.created_at)}
      </Typography>
    );
  };
  // Equity Value and Price / Share are restated in the NAV reporting currency
  // per field, and only when a rate was available -- so the label has to come
  // from converted_fields, exactly as the grid's own cells do. Labelling a
  // converted figure with the cap table currency is the failure this avoids.
  const snapshotCurrency = (field: string) =>
    (previousValuation?.converted_fields?.includes(field) && previousValuation?.reporting_currency) || currencyCode;
  const todaysPrice = record?.todays_price;
  // The report's current price is the valuation finalized today. Deferring
  // would leave Today's Price on the previous valuation.
  const nextDayBlocked = Boolean(record?.valuation_finalized_today) && !reviewFieldsLocked;
  const todaysPriceCurrency =
    (todaysPrice?.converted_fields?.includes('common_share_value') && todaysPrice?.reporting_currency) ||
    todaysPrice?.cap_table_currency ||
    currencyCode;

  return (
    <Box>
      <Typography sx={{ color: colors.slate500, fontSize: 12, marginBottom: 1.5 }}>{row?.companyName ?? ''}</Typography>
      {reviewFieldsLocked && lockedReason && (
        <Typography
          data-testid="review-price-locked-reason"
          sx={{ color: colors.slate500, fontSize: 12, fontStyle: 'italic', marginBottom: 1.5 }}>
          {lockedReason}
        </Typography>
      )}
      {isCarryingAnAdjustment && (
        <Typography
          data-testid="review-price-carried-adjustment"
          sx={{ color: colors.slate700, fontSize: 12, fontWeight: 600, marginBottom: 1.5 }}>
          {nextDayBlocked
            ? 'A price adjustment was deferred onto today, and a valuation has since been finalized. The new price has to land today.'
            : 'A price adjustment was deferred onto today and is already approved. Post the new valuation, or defer it again for one more trading day.'}
        </Typography>
      )}

      <Box display="flex" flexDirection="column">
        {/* The analyst's sign-off: Scalar-managed only. */}
        {!fundManaged && (
          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  id="review-price-reviewed-by-scalar"
                  size="small"
                  checked={formValues.reviewed_by_scalar}
                  disabled={reviewFieldsLocked}
                  onChange={event => setFormValues({ ...formValues, reviewed_by_scalar: event.target.checked })}
                />
              }
              label={
                <Typography sx={{ color: colors.slate700, fontSize: 12 }}>
                  {DAILY_NAV_REVIEW_FIELD_LABELS.reviewedBy}
                </Typography>
              }
            />
            {renderAudit(DailyNavReviewActionLog.field.REVIEWED_BY_SCALAR)}
          </Box>
        )}
        <Box display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            control={
              <Checkbox
                id="review-price-discussed-with-fund"
                size="small"
                checked={formValues.discussed_with_fund}
                disabled={reviewFieldsLocked}
                onChange={event => setFormValues({ ...formValues, discussed_with_fund: event.target.checked })}
              />
            }
            label={
              <Typography sx={{ color: colors.slate700, fontSize: 12 }}>
                {DAILY_NAV_REVIEW_FIELD_LABELS.discussedWithAdvisor}
              </Typography>
            }
          />
          {renderAudit(DailyNavReviewActionLog.field.DISCUSSED_WITH_FUND)}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={1} marginY={1.5}>
        <ToggleButtonGroup
          id="review-price-price-adjusted"
          exclusive
          size="small"
          disabled={reviewFieldsLocked}
          value={priceAdjustedValue}
          onChange={(_event, newValue) => {
            if (newValue === 'yes') {
              setFormValues({ ...formValues, price_adjusted: true });
            } else if (newValue === 'no') {
              // Dropped here too, so the hint updates before the save returns.
              setFormValues({ ...formValues, price_adjusted: false, price_adjustment_timing: null });
            }
          }}
          sx={toggleGroupSx}>
          <ToggleButton value="no">No</ToggleButton>
          <ToggleButton value="yes">Yes</ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={fieldLabelSx}>Price adjusted</Typography>
        {renderAudit(DailyNavReviewActionLog.field.PRICE_ADJUSTED)}
      </Box>

      {formValues.price_adjusted === true && (
        <Box display="flex" alignItems="center" gap={1} marginY={1.5}>
          <ToggleButtonGroup
            id="review-price-adjustment-timing"
            exclusive
            size="small"
            disabled={reviewFieldsLocked}
            value={formValues.price_adjustment_timing}
            onChange={(_event, newValue) => {
              if (newValue === 'next_day' && nextDayBlocked) {
                return;
              }
              if (newValue) {
                setFormValues({ ...formValues, price_adjustment_timing: newValue });
              }
            }}
            sx={toggleGroupSx}>
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton
              value="next_day"
              disabled={nextDayBlocked}
              sx={
                nextDayBlocked
                  ? {
                    '&&.Mui-disabled': {
                      borderColor: colors.error,
                      color: colors.error,
                      opacity: 1,
                    },
                    '&&.Mui-disabled.Mui-selected': {
                      backgroundColor: 'transparent',
                      borderColor: colors.error,
                      color: colors.error,
                    },
                  }
                  : undefined
              }>
              Next trading day
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={fieldLabelSx}>New price expected</Typography>
          {renderAudit(DailyNavReviewActionLog.field.PRICE_ADJUSTMENT_TIMING)}
        </Box>
      )}
      {formValues.price_adjusted === true && nextDayBlocked && (
        <Typography
          data-testid="review-price-next-day-blocked"
          sx={{ color: colors.error, fontSize: 11, marginBottom: 1.5 }}>
          A valuation was finalized today, so the new price has to land today. Next trading day would make Today&apos;s
          Price differ from the report&apos;s current price.
        </Typography>
      )}
      {formValues.price_adjusted === true && formValues.price_adjustment_timing === 'next_day' && !nextDayBlocked && (
        <Typography sx={{ color: colors.slate500, fontSize: 11, fontStyle: 'italic', marginBottom: 1.5 }}>
          Today will be finalized on the previous valuation, and tomorrow&apos;s row will read &ldquo;Needs New
          Price&rdquo; until the new one is posted.
        </Typography>
      )}

      {previousValuation && (
        <Table
          size="small"
          sx={{
            border: `1px solid ${colors.border}`,
            marginY: 1.5,
            '& th, & td': { border: 'none', borderTop: `1px solid ${colors.border}`, paddingX: 0.75, paddingY: 0.5 },
          }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: colors.tableHeaderBackground }} />
              <TableCell
                align="center"
                sx={{
                  backgroundColor: colors.tableHeaderBackground,
                  color: colors.slate800,
                  fontSize: 11,
                  fontWeight: 600,
                }}>
                {`${shortDate(previousValuation.measurement_date_value)} (previous valuation)`}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ color: colors.slate800, fontSize: 11, fontWeight: 600 }}>
                Equity Value
              </TableCell>
              <TableCell align="right" sx={{ color: colors.slate800, fontSize: 11 }}>
                {formatCurrency(previousValuation.equity_value, snapshotCurrency('equity_value'))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ color: colors.slate800, fontSize: 11, fontWeight: 600 }}>
                Price/Share
              </TableCell>
              <TableCell align="right" sx={{ color: colors.slate800, fontSize: 11 }}>
                {formatCurrency(previousValuation.common_share_value, snapshotCurrency('common_share_value'), 2)}
              </TableCell>
            </TableRow>
            {/* With a price adjustment, today's price is the most recent FINAL
                valuation -- the same number the grid's Today's Price column shows. */}
            {formValues.price_adjusted === true && todaysPrice && (
              <TableRow data-testid="review-price-todays-price">
                <TableCell component="th" scope="row" sx={{ color: colors.slate800, fontSize: 11, fontWeight: 600 }}>
                  {`Today's Price (${shortDate(todaysPrice.measurement_date_value)})`}
                </TableCell>
                <TableCell align="right" sx={{ color: colors.slate800, fontSize: 11 }}>
                  {formatCurrency(todaysPrice.common_share_value, todaysPriceCurrency, 2)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Box marginTop={1.5}>
        <Typography sx={fieldLabelSx}>Review Notes</Typography>
        {isEmpty(notes) ? (
          <Typography sx={{ color: colors.slate500, fontSize: 11, fontStyle: 'italic', marginY: 0.5 }}>
            No notes yet.
          </Typography>
        ) : (
          <Box marginY={0.5}>
            {notes.map(note => (
              <Box
                key={note.id}
                display="flex"
                alignItems="baseline"
                justifyContent="space-between"
                gap={1}
                sx={{ borderBottom: `1px solid ${colors.border}`, paddingY: 0.5 }}>
                <Box>
                  {note.body && <Typography sx={{ color: colors.slate700, fontSize: 11 }}>{note.body}</Typography>}
                  {(note.attachments ?? []).map(attachment => (
                    <Button
                      key={attachment.id}
                      size="small"
                      startIcon={<AttachFileOutlined sx={{ fontSize: 12 }} />}
                      data-testid={`review-price-note-attachment-${attachment.id}`}
                      onClick={() => attachment.id != null && onDownloadAttachment(attachment.id)}
                      sx={{ color: colors.navy, display: 'flex', fontSize: 11, padding: 0, textTransform: 'none' }}>
                      {`${attachment.filename}.${attachment.file_type}`}
                    </Button>
                  ))}
                  <Typography sx={{ color: colors.slate500, fontSize: 10 }}>
                    {note.created_by_name || 'Unknown'} · {formatNoteTime(note.created_at)}
                  </Typography>
                </Box>
                {!notesLocked && onDeleteNote && (
                  <Button
                    size="small"
                    onClick={() => note.id != null && onDeleteNote(note.id)}
                    sx={{ color: colors.slate500, fontSize: 10, minWidth: 0, padding: 0, textTransform: 'none' }}>
                    Delete
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
        {!notesLocked && (
          <>
            <TextField
              id="review-price-review-notes"
              placeholder="Add a note…"
              multiline
              minRows={3}
              fullWidth
              margin="dense"
              value={noteDraft}
              onChange={event => setNoteDraft(event.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': { fontSize: 12 },
                '& fieldset': { borderColor: colors.border },
              }}
            />
            {noteFiles.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={0.5} marginTop={0.5}>
                {noteFiles.map((file, index) => (
                  <Chip
                    key={`${file.name}-${file.lastModified}`}
                    size="small"
                    label={file.name}
                    data-testid={`review-price-pending-file-${index}`}
                    onDelete={() => setNoteFiles(noteFiles.filter((_, fileIndex) => fileIndex !== index))}
                    sx={{ fontSize: 11 }}
                  />
                ))}
              </Box>
            )}
            {/* Files go to Documents > Daily NAV > <Company> > <this report date>. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              data-testid="review-price-attach-file-input"
              onChange={event => {
                const chosen = Array.from(event.target.files ?? []);
                setNoteFiles([...noteFiles, ...chosen]);
                // Reset so choosing the same file again still fires onChange.
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            />
            <Button
              id="review-price-attach-file-button"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<AttachFileOutlined sx={{ fontSize: 14 }} />}
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                color: colors.slate700,
                fontSize: 11,
                marginRight: 0.5,
                marginTop: 0.5,
                paddingX: 1.25,
                paddingY: 0.25,
                textTransform: 'none',
              }}>
              Attach file
            </Button>
            <Button
              id="review-price-add-note-button"
              onClick={onAddNote}
              disabled={!noteDraft.trim() && noteFiles.length === 0}
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                color: colors.slate700,
                fontSize: 11,
                marginTop: 0.5,
                paddingX: 1.25,
                paddingY: 0.25,
                textTransform: 'none',
              }}>
              Add note
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReviewPriceDialogContent;
